type BlockCloser = "end" | "}" | "end note"

interface BlockGrammar {
  opensWith: (line: string) => BlockCloser | null
}

const FRONTMATTER_DELIMITER = "---"
const COMMENT_PREFIX = "%%"

const FLOWCHART_OPENERS = /^subgraph\b/
const SEQUENCE_OPENERS = /^(loop|alt|opt|par|critical|rect|box|break)\b/
const BLOCK_DIAGRAM_OPENERS = /^block:/
const MULTILINE_NOTE_OPENER = /^note\s+(left|right)\s+of\b[^:]*$/i

function braceOpener(line: string): BlockCloser | null {
  return line.endsWith("{") ? "}" : null
}

const flowchartGrammar: BlockGrammar = {
  opensWith: (line) => (FLOWCHART_OPENERS.test(line) ? "end" : null),
}

const sequenceGrammar: BlockGrammar = {
  opensWith: (line) => (SEQUENCE_OPENERS.test(line) ? "end" : null),
}

const stateGrammar: BlockGrammar = {
  opensWith: (line) => (MULTILINE_NOTE_OPENER.test(line) ? "end note" : braceOpener(line)),
}

const braceGrammar: BlockGrammar = { opensWith: braceOpener }

const blockDiagramGrammar: BlockGrammar = {
  opensWith: (line) => (BLOCK_DIAGRAM_OPENERS.test(line) ? "end" : null),
}

const noBlocksGrammar: BlockGrammar = { opensWith: () => null }

const GRAMMAR_BY_DIAGRAM: ReadonlyMap<string, BlockGrammar> = new Map([
  ["flowchart", flowchartGrammar],
  ["flowchart-elk", flowchartGrammar],
  ["graph", flowchartGrammar],
  ["sequencediagram", sequenceGrammar],
  ["statediagram", stateGrammar],
  ["statediagram-v2", stateGrammar],
  ["classdiagram", braceGrammar],
  ["classdiagram-v2", braceGrammar],
  ["erdiagram", braceGrammar],
  ["requirementdiagram", braceGrammar],
  ["c4context", braceGrammar],
  ["c4container", braceGrammar],
  ["c4component", braceGrammar],
  ["c4dynamic", braceGrammar],
  ["c4deployment", braceGrammar],
  ["block-beta", blockDiagramGrammar],
])

function isIgnorableLine(line: string): boolean {
  return line === "" || line.startsWith(COMMENT_PREFIX)
}

interface SplitDiagram {
  header: string
  headerLines: string[]
  statements: string[]
}

function statementLinesAfterHeader(lines: string[]): SplitDiagram | null {
  let index = 0
  if (lines[0]?.trim() === FRONTMATTER_DELIMITER) {
    const frontmatterEnd = lines.findIndex((line, at) => at > 0 && line.trim() === FRONTMATTER_DELIMITER)
    if (frontmatterEnd === -1) return null
    index = frontmatterEnd + 1
  }
  while (index < lines.length && isIgnorableLine(lines[index].trim())) index += 1
  if (index >= lines.length) return null
  return { header: lines[index].trim(), headerLines: lines.slice(0, index + 1), statements: lines.slice(index + 1) }
}

function withoutTrailingEmptyBlockOpeners(grammar: BlockGrammar, statements: string[]): string[] {
  let end = statements.length
  while (end > 0) {
    const line = statements[end - 1].trim()
    if (isIgnorableLine(line)) {
      end -= 1
      continue
    }
    if (grammar.opensWith(line) === null) break
    end -= 1
  }
  return statements.slice(0, end)
}

function unclosedBlockClosers(grammar: BlockGrammar, statements: string[]): BlockCloser[] {
  const openBlocks: BlockCloser[] = []
  for (const rawLine of statements) {
    const line = rawLine.trim()
    if (isIgnorableLine(line)) continue
    if (line === openBlocks[openBlocks.length - 1]) {
      openBlocks.pop()
      continue
    }
    const closer = grammar.opensWith(line)
    if (closer) openBlocks.push(closer)
  }
  return openBlocks.reverse()
}

export function mermaidStreamPreview(fenceBody: string): string {
  const lastNewline = fenceBody.lastIndexOf("\n")
  if (lastNewline === -1) return ""
  const completedLines = fenceBody.slice(0, lastNewline + 1)
  const parsed = statementLinesAfterHeader(completedLines.split("\n"))
  if (!parsed) return ""
  const diagramType = parsed.header.split(/\s+/)[0].toLowerCase()
  const grammar = GRAMMAR_BY_DIAGRAM.get(diagramType) ?? noBlocksGrammar
  const statements = withoutTrailingEmptyBlockOpeners(grammar, parsed.statements)
  const shownLines = [...parsed.headerLines, ...statements]
  const closers = unclosedBlockClosers(grammar, statements)
  return [...shownLines, ...closers].map((line) => `${line}\n`).join("")
}
